// Parses a GPX or TCX file entirely in the browser — no upload to any
// server, no external API, no cost. Covers the export format offered by
// virtually every watch/app (Garmin Connect, Coros, Suunto, Apple Health
// export tools, etc.) via "Export activity" -> GPX or TCX.

export type ParsedTrack = {
  startedAt: string; // ISO timestamp
  durationMinutes: number;
  distanceKm: number;
  avgSpeedKmh: number;
  maxSpeedKmh: number;
  lat: number | null;
  lon: number | null;
  points: [number, number][]; // [lat, lon][]
};

function haversineKm(a: [number, number], b: [number, number]) {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLon = ((b[1] - a[1]) * Math.PI) / 180;
  const lat1 = (a[0] * Math.PI) / 180;
  const lat2 = (b[0] * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function summarize(points: [number, number][], timestamps: Date[]): ParsedTrack {
  if (points.length === 0 || timestamps.length === 0) {
    throw new Error("No track points found in this file.");
  }

  let distanceKm = 0;
  let maxSpeedKmh = 0;
  for (let i = 1; i < points.length; i++) {
    const segKm = haversineKm(points[i - 1], points[i]);
    distanceKm += segKm;
    const segHours = (timestamps[i].getTime() - timestamps[i - 1].getTime()) / 3_600_000;
    if (segHours > 0) {
      const speed = segKm / segHours;
      if (Number.isFinite(speed)) maxSpeedKmh = Math.max(maxSpeedKmh, speed);
    }
  }

  const startedAt = timestamps[0];
  const endedAt = timestamps[timestamps.length - 1];
  const durationMinutes = Math.max(1, Math.round((endedAt.getTime() - startedAt.getTime()) / 60_000));
  const avgSpeedKmh = distanceKm / (durationMinutes / 60);

  return {
    startedAt: startedAt.toISOString(),
    durationMinutes,
    distanceKm: Math.round(distanceKm * 100) / 100,
    avgSpeedKmh: Math.round(avgSpeedKmh * 10) / 10,
    maxSpeedKmh: Math.round(maxSpeedKmh * 10) / 10,
    lat: points[0][0],
    lon: points[0][1],
    points,
  };
}

function parseGpx(doc: Document): ParsedTrack {
  const trkpts = Array.from(doc.getElementsByTagName("trkpt"));
  if (trkpts.length === 0) throw new Error("No <trkpt> points found — is this a valid GPX track?");

  const points: [number, number][] = [];
  const timestamps: Date[] = [];
  for (const pt of trkpts) {
    const lat = parseFloat(pt.getAttribute("lat") ?? "");
    const lon = parseFloat(pt.getAttribute("lon") ?? "");
    const timeEl = pt.getElementsByTagName("time")[0];
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || !timeEl?.textContent) continue;
    points.push([lat, lon]);
    timestamps.push(new Date(timeEl.textContent));
  }
  return summarize(points, timestamps);
}

function parseTcx(doc: Document): ParsedTrack {
  const trackpoints = Array.from(doc.getElementsByTagName("Trackpoint"));
  if (trackpoints.length === 0) throw new Error("No <Trackpoint> points found — is this a valid TCX file?");

  const points: [number, number][] = [];
  const timestamps: Date[] = [];
  for (const tp of trackpoints) {
    const lat = tp.getElementsByTagName("LatitudeDegrees")[0]?.textContent;
    const lon = tp.getElementsByTagName("LongitudeDegrees")[0]?.textContent;
    const time = tp.getElementsByTagName("Time")[0]?.textContent;
    if (!lat || !lon || !time) continue;
    points.push([parseFloat(lat), parseFloat(lon)]);
    timestamps.push(new Date(time));
  }
  return summarize(points, timestamps);
}

export async function parseTrackFile(file: File): Promise<ParsedTrack> {
  const name = file.name.toLowerCase();
  if (!name.endsWith(".gpx") && !name.endsWith(".tcx")) {
    throw new Error("Please upload a .gpx or .tcx file — export it from your watch's app first.");
  }

  const text = await file.text();
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Couldn't read that file — it doesn't look like valid XML.");
  }

  return name.endsWith(".gpx") ? parseGpx(doc) : parseTcx(doc);
}
